import React, { useState, useEffect } from 'react';
import './StoryPrompt.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { Character } from '../../types/character';
import { StoryFormData } from '../../types/StoryFormData';
import { createStory } from '../../api/stories';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import BookCreationPopup from '../BookLoading/BookCreationPopup';

const StoryReview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formData = location.state as StoryFormData;
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Initialize formData with default story_preferences if it doesn't exist
  const initialFormData = {
    ...formData,
    story_preferences: formData.story_preferences || {
      is_public: true,
      longer_text: false,
      rhyming_story: false,
      page_count: 4
    }
  };
  
  const [isCreating, setIsCreating] = useState(false);
  const [localFormData] = useState<StoryFormData>(initialFormData);
  const [showPopup, setShowPopup] = useState(false);

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const result = await createStory(localFormData);
      setShowPopup(true);
      
      // Store the data in case we need it later
      localStorage.setItem('storyCreationData', JSON.stringify({
        storyId: result.id,
        status: result.status,
        title: localFormData.title
      }));
      
    } catch (error) {
      console.error('Error creating story:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    navigate('/profile');
  };

  return (
    <div className="story-prompt">
      {/* Hero title section with background */}
      <div className="title-hero-wrapper">
        <div className="title-container">
          <div className="self-stretch text-center justify-start text-fuchsia-500 text-3xl font-normal font-['Combo'] tracking-wide">Create a Book</div>
          <div className="self-stretch text-center justify-start text-black text-5xl font-medium font-['Baloo_2'] leading-[66px]">Create your personalized ai storybook!</div>
        </div>
      </div>
      
      {/* Progress Bar - Changed to step 3 */}
      <ProgressBar currentStep={3} />

      <div className="story-prompt-container">
        <div className="back-navigation-section">
          <div className="back-nav-bg"></div>
          <div className="back-nav-content">
            <button className="back-button" onClick={() => navigate('/create/2-preferences', { state: localFormData })}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 19L5 12L12 5" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 12H5" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="review-page-title">Review Page</div>
          </div>
        </div>

        <div className="container-content">
          {/* Story Outline Section */}
          <div className="review-section">
            <h2 className="section-title">Story Outline</h2>
            
            <div className="story-details">
              <div className="detail-row">
                <label>Title:</label>
                <p>{formData.title}</p>
              </div>

              <div className="detail-row">
                <label>Description:</label>
                <p>{formData.description}</p>
              </div>

              {formData.characters && formData.characters.length > 0 && (
                <div className="detail-section">
                  <h3>Characters:</h3>
                  <div className="characters-list">
                    {formData.characters.map((character: Character) => (
                      <div key={character.id} className="character-item">
                        <img src={character.image || '/default-avatar.png'} alt={character.name} />
                        <div className="character-info">
                          <span className="character-name">{character.name}</span>
                          <span className="character-description">
                            {character.age && 
                              `- a ${character.age} years old ${character.gender || ''}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Story Preferences Section */}
          <div className="review-section">
            <h2 className="section-title">Story Preferences</h2>
            
            <div className="story-details">
              <div className="detail-section">
                <h3>Visibility</h3>
                <div className="detail-row">
                  <label>Public:</label>
                  <p>{localFormData.story_preferences.is_public ? 'Yes' : 'No'}</p>
                </div>
                <p className="option-description">
                  {localFormData.story_preferences.is_public 
                    ? 'Your book will be public, so other people can see it and read it.' 
                    : 'Your book will be private, only you can see it and read it.'}
                </p>
              </div>

              <div className="detail-section">
                <h3>Story Style</h3>
                <div className="detail-row">
                  <label>Longer text:</label>
                  <p>{localFormData.story_preferences.longer_text ? 'Yes' : 'No'}</p>
                </div>
                <div className="detail-row">
                  <label>Rhyming story:</label>
                  <p>{localFormData.story_preferences.rhyming_story ? 'Yes' : 'No'}</p>
                </div>
              </div>

              <div className="detail-section">
                <h3>Book Details</h3>
                <div className="detail-row">
                  <label>Page count:</label>
                  <p>{localFormData.story_preferences.page_count} pages</p>
                </div>
              </div>
            </div>
          </div>

          <div className="button-container">
            <button className="next-btn" onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </div>

      {/* Use the BookCreationPopup component */}
      {showPopup && <BookCreationPopup onClose={handleClosePopup} />}
    </div>
  );
};

export default StoryReview; 